import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { debounce } from "lodash";
import { Button, Drawer, Form, Select, TreeSelect } from "antd";
import { DatePicker, useJalaliLocaleListener } from "antd-jalali";
import PropTypes from "prop-types";
import Moment from "moment-jalaali";
import dayjs from "dayjs";

import { Modals, Types } from "scripts/settings";
import API from "scripts/api";
import Resources from "scripts/resources";
import Rules from "scripts/rules";

import Upload from "components/Upload";
import { ObjectNumbersToEnglish } from "scripts/helpers";

const Component = ({
	data = {},
	onUpdate,
	options: { categories = [], cities = [], types = [] },
}) => {
	const initialState = {
		jobs: [],
	};
	const [state, setState] = useState(initialState);
	const { jobs, submitting, visible } = state;
	const { id, title } = data;
	const { hash, pathname, search } = useLocation();
	const navigate = useNavigate();
	const [form] = Form.useForm();

	const handleSearch = debounce((search) => {
		if (search?.length > 1) {
			API.job
				.list({
					paginate: false,
					search,
					status: "approved",
					subscribed: "true",
				})
				.then(({ data }) => {
					setState((prevState) => ({
						...prevState,
						jobs: data.map((o) => ({ value: o.id, label: o.title })),
					}));
				})
				.catch(() => {});
		}
	}, 500);

	const handleClose = () => {
		navigate(-1);
	};

	const handleSubmit = () => {
		if (!submitting) {
			form.validateFields().then((values) => {
				setState((prevState) => ({ ...prevState, submitting: true }));

				values = ObjectNumbersToEnglish(values);

				const [from, to] = values.date;

				values.coverId = values.cover?.id;
				values.startAt = Moment(from.$d).format(
					process.env.REACT_APP_DATE_FORMAT
				);

				values.expireAt = Moment(to.$d).format(
					process.env.REACT_APP_DATE_FORMAT
				);

				delete values.cover;
				delete values.date;

				const action = id
					? API.banner.update(id, values)
					: API.banner.create(values);

				action
					.then(() => {
						onUpdate(null, true);
						handleClose();
					})
					.catch(() => {
						setState((prevState) => ({
							...prevState,
							submitting: false,
						}));
					});
			});
		}
	};

	const componentDidUpdate = () => {
		switch (hash) {
			case `#${Modals.CREATE}`: {
				setState((prevState) => ({ ...prevState, visible: true }));

				break;
			}
			case `#${Modals.EDIT}`: {
				if (id) {
					setState((prevState) => ({
						...prevState,
						jobs: [{ value: data.job?.id, label: data.job.title }],
						visible: true,
					}));

					form.setFieldsValue({
						...data,
						categoryId: data.category?.id,
						cityId: data.city?.id,
						jobId: data.job?.id,
						type: data.type?.en,
						date: [dayjs(data.startAt), dayjs(data.expireAt)],
					});
				} else {
					navigate(`${pathname}${search}`, { replace: true });
				}

				break;
			}
			default: {
				if (visible) form.resetFields();

				setState(initialState);

				break;
			}
		}
	};

	useEffect(componentDidUpdate, [hash]);

	useJalaliLocaleListener();

	const footer = (
		<>
			<Button type="primary" onClick={handleSubmit} loading={submitting}>
				{Resources.confirm}
			</Button>
			<Button onClick={() => handleClose()} loading={submitting}>
				{Resources.cancel}
			</Button>
		</>
	);

	return (
		<Drawer
			className="sp-form-drawer"
			footer={footer}
			onClose={() => handleClose()}
			title={
				id
					? `${Resources.edit} ${Resources.banner} "${title || "-"}"`
					: `${Resources.add} ${Resources.banner}`
			}
			maskClosable={false}
			open={visible}
			width={414}
		>
			<Form layout="vertical" form={form}>
				<Form.Item name="jobId" label={Resources.job} rules={Rules.required}>
					<Select
						options={jobs}
						onSearch={handleSearch}
						filterOption={false}
						showArrow={false}
						showSearch
					/>
				</Form.Item>
				<Form.Item name="cityId" label={Resources.city} rules={Rules.required}>
					<Select options={cities} optionFilterProp="label" showSearch />
				</Form.Item>
				<Form.Item name="categoryId" label={Resources.category}>
					<TreeSelect
						treeData={categories}
						treeNodeFilterProp="label"
						showSearch
					/>
				</Form.Item>
				<Form.Item name="date" label={Resources.date} rules={Rules.required}>
					<DatePicker.RangePicker />
				</Form.Item>
				<Form.Item name="cover" label={Resources.image} rules={Rules.required}>
					<Upload aspect={1.8} section={Types.upload.banner} />
				</Form.Item>
			</Form>
		</Drawer>
	);
};

Component.propTypes = {
	data: PropTypes.object,
	onUpdate: PropTypes.func.isRequired,
	options: PropTypes.object.isRequired,
};

export default Component;
